/**
 * Member Hub phase 5a — the Settings tab and `/profile`, its handle-less fallback (pure; the
 * components are `components/community/MemberSettings.tsx`, the pages `pages/profile/*.tsx`).
 *
 * `/profile` (and, since plan D1 2026-10-26, `/profile/notifications` and `/profile/settings`)
 * is a redirector + fallback, never a second home: a reader WITH a member page is sent on to the
 * hub tab that matches the old address; anyone else (hidden profile, unclaimed device, no
 * device, signed out) gets the shared account shell — "How you appear" on `/profile` beside My
 * gear, Sign-in and Your data on `/profile/settings`.
 */
import { accountHref } from 'shared-components/account/routes'
import { memberHubHref, tierLabel, youState } from './model'
import { NOTIFY_SETTINGS_ANCHOR } from './hub-notifications'
import { DRAFT_ANCHOR } from './draft-card'
import { OWNER_EMAILS_ANCHOR, profileOpensGear } from 'lib/owner-notes/band-copy'
import {
  commentNameCanSave,
  commentNameLine,
  inheritedIdentityName,
  inheritsFromGoogle,
  NAME_COPY,
} from 'shared-components/account/name'

// The comment name's rules live with the shared account module (commentHub only, both brands);
// re-exported so this file stays the one import for the hub's Settings.
export { commentNameCanSave, commentNameLine, inheritedIdentityName, inheritsFromGoogle }

/**
 * The reader's handle when DealHunt serves them a member page — `youState`'s `member` kind
 * (claimed AND `community.profile_public !== false`, 7839ca41: "the exact set DealHunt serves a
 * page for") — else null. `settings.profile_public` is the same value in the same `/me` (DealHunt
 * copies the community block's), but it is the one still there when the best-effort `community`
 * block is omitted, so both must allow it. The one predicate for the `/profile` redirect and the
 * avatar menu.
 */
export function ownHubHandle(
  me: (Parameters<typeof youState>[0] & { settings?: { profile_public?: boolean } | null }) | null
): string | null {
  const state = youState(me)
  return state.kind === 'member' && me?.settings?.profile_public !== false ? state.handle : null
}

export type ProfileRoute =
  | { kind: 'replace'; href: string }
  | { kind: 'render'; tab: 'settings' | 'gear' }

/**
 * Where `/profile` sends a reader. With a hub page (`hubHandle`) → `router.replace` to the hub
 * tab the old address meant: `?tab=gear` → Gear, the owner emails' `#owner-emails` → the
 * Notifications tab's "Notify me" grid, the writer's `#draft` → the Overview's "Your draft",
 * anything else → Settings. Without one → rendered here,
 * on My gear for the same two deep links (`profileOpensGear`), else on Settings.
 */
export function profileRoute(
  hubHandle: string | null,
  queryTab: unknown,
  hash: string,
  trailingSlash: boolean
): ProfileRoute {
  if (!hubHandle) {
    return { kind: 'render', tab: profileOpensGear(queryTab, hash) ? 'gear' : 'settings' }
  }
  if (queryTab === 'gear') {
    return { kind: 'replace', href: memberHubHref(hubHandle, trailingSlash, 'gear') }
  }
  // The writer's "Find it in your profile" when the browser did not know the hub handle.
  if (hash === `#${DRAFT_ANCHOR}`) {
    return { kind: 'replace', href: `${memberHubHref(hubHandle, trailingSlash)}#${DRAFT_ANCHOR}` }
  }
  if (hash === `#${OWNER_EMAILS_ANCHOR}`) {
    return {
      kind: 'replace',
      href: `${memberHubHref(hubHandle, trailingSlash, 'notifications')}#${NOTIFY_SETTINGS_ANCHOR}`,
    }
  }
  return { kind: 'replace', href: memberHubHref(hubHandle, trailingSlash, 'settings') }
}

/**
 * `/profile/notifications` and `/profile/settings` (plan D1, 2026-10-26) follow `/profile`'s rule:
 * a reader WITH a hub page is sent to the hub tab of the same name (the hash rides along, so the
 * emails' and the bell's `#notify-settings` still lands on the grid); anyone else (null) gets
 * the account page rendered there.
 */
export function profilePageRoute(
  hubHandle: string | null,
  page: 'notifications' | 'settings',
  hash: string,
  trailingSlash: boolean
): string | null {
  if (!hubHandle) {
    return null
  }
  return `${memberHubHref(hubHandle, trailingSlash, page)}${hash}`
}

/** `/profile?hidden=1` — set by Settings after the reader hid their page (its hub URL now 404s). */
export const PROFILE_HIDDEN_PARAM = 'hidden'

export const SETTINGS_COPY = {
  privateLine: 'Settings are private to this member.',
  appearTitle: 'How you appear',
  pseudonymLabel: 'Owner reviews & member page',
  pseudonymRename: 'Pseudonym · can be changed once',
  pseudonymSet: 'Pseudonym · set once, can’t be changed',
  /** Member identity (2026-10-10): the two tiles of "How you appear". The old line promised the
   *  real name never shows — it can now, by the member's choice (ticket §2 decision 3). */
  appearIntro: 'Choose how other members see you.',
  appearName: 'Your name and photo',
  appearNameHint: 'Your comment name and Google photo, as on your replies.',
  /** The `*Linked` lines replace the Google ones when the account's primary sign-in is not
   *  Google (an email-first account, `inheritsFromGoogle`) — labels only. */
  appearNameHintLinked: 'Your comment name and linked account photo, as on your replies.',
  appearPseudonym: 'A pseudonym',
  appearPseudonymHint: 'Initials and your handle only.',
  appearHandleStays: 'Your @handle stays the same for links and mentions.',
  appearUpdates: 'Other pages update within 5 minutes.',
  appearEditName: 'Edit name',
  appearPendingConsent: 'You appear as a pseudonym until you choose.',
  /** The one-time banner for members who joined before names showed (`identityBannerWanted`). */
  bannerTitle: 'Show your name and photo?',
  bannerBody: (name: string): string =>
    `You'd appear as ${name} on the leaderboard, your member page and your posts. You can switch back any time in Settings.`,
  bannerShow: 'Show my name and photo',
  bannerKeep: 'Keep my pseudonym',
  ...NAME_COPY,
  publicTitle: 'Public member page',
  publicDetail:
    'Overview and Gear are visible to everyone: the gear you reviewed, your badges and your leaderboard place. Turn off to hide the page; your reviews stay on product pages. Changes can take a few minutes.',
  hiddenNote: 'Your member page is hidden. Your reviews stay on product pages.',
  backOn: 'Your member page is back.',
  /** Phase 5a: "Your data" is commentHub's export and delete only — DealHunt's (owner reviews)
   *  is phase 5b, designed separately. */
  dataScope:
    'Covers your profile, comments, reports and votes. Owner reviews are handled separately — contact support.',
} as const

/** The identity facts "How you appear" and the banner read from `/me` (DealHunt B.3). */
type IdentityMe = {
  profile: { claimed: boolean }
  settings?: { show_identity?: boolean } | null
  identity?: {
    display_name: string | null
    show_identity: boolean
    acknowledged: boolean
    synced: boolean
  }
} | null

export type AppearChoice =
  /** No identity block (older DealHunt, table not ready) or an unclaimed device: the pseudonym,
   *  shown read-only — there is nothing to switch. */
  { kind: 'fixed' } | { kind: 'choose'; showIdentity: boolean; pending: boolean }

/**
 * What the two tiles say. `pending`: an existing member who has not answered yet — DealHunt shows
 * them as the pseudonym until they do (consent gate), whatever `show_identity` holds.
 */
export function appearChoice(me: IdentityMe): AppearChoice {
  const identity = me?.identity
  if (!me || !me.profile.claimed || !identity) {
    return { kind: 'fixed' }
  }
  const pending = identity.acknowledged === false
  return {
    kind: 'choose',
    showIdentity: !pending && (me.settings?.show_identity ?? identity.show_identity),
    pending,
  }
}

/**
 * The one-time banner (ticket §3.3, contract B.3): a claimed member whose identity DealHunt has
 * synced (a row exists) but who has not answered, and who has a name to offer — never for a
 * profile without a row, and never a sentence with an empty name in it.
 */
export function identityBannerWanted(me: IdentityMe): boolean {
  const identity = me?.identity
  return Boolean(
    me?.profile.claimed &&
      identity &&
      identity.synced === true &&
      identity.acknowledged === false &&
      identity.display_name
  )
}

/** The pseudonym card's line: the one-time rename while DealHunt still allows it. */
export function pseudonymLine(canRename: boolean): string {
  return canRename ? SETTINGS_COPY.pseudonymRename : SETTINGS_COPY.pseudonymSet
}

/** What follows a stored `profile_public` change, by where Settings sits. */
export type PublicChangeOutcome =
  | { kind: 'navigate'; href: string }
  | { kind: 'note'; note: 'hidden' | 'back' }

/**
 * Hidden from the hub: its page 404s from now on (an edge copy may linger ~5 min), so the reader
 * leaves for `/profile?hidden=1` — the same Settings with the note. Anything else says so in place
 * ("back" links the hub page again).
 */
export function afterPublicChange(
  where: 'hub' | 'profile',
  on: boolean,
  profileHref: string
): PublicChangeOutcome {
  if (!on && where === 'hub') {
    return { kind: 'navigate', href: `${profileHref}?${PROFILE_HIDDEN_PARAM}=1` }
  }
  return { kind: 'note', note: on ? 'back' : 'hidden' }
}

/** After the one-time rename on the hub, the tab's address follows the handle (the old one no
 *  longer resolves to this member); on `/profile` nothing moves. */
export function afterRename(
  where: 'hub' | 'profile',
  handle: string,
  trailingSlash: boolean
): string | null {
  return where === 'hub' ? memberHubHref(handle, trailingSlash, 'settings') : null
}

// ── the avatar menu (Member Hub 2a) ───────────────────────────────────────────

export interface AccountMenuLinks {
  /** The reader's handle when they have a member page (the identity block), else null. */
  hubHandle: string | null
  gear: { href: string; meta?: string }
  settings: string
  /** The member's Stats tab; null without a member page — a hidden or unclaimed profile has no
   *  stats page to open. */
  stats: string | null
  /** The member's Overview (the menu head's link and its Overview row); null without a page. */
  overview: string | null
  /** The menu head's second line for a member — "Listener · 1,240 pts · @handle" (the standing
   *  when `/me` carries it, else the handle alone); null without a page (the head shows email). */
  line: string | null
}

/**
 * Whether the avatar menu's head holds its second line empty: a reader this browser remembers as
 * a member (`rememberedHandle`) whose `/me` has not answered yet. Once the read settles — an
 * answer, a failure, or no owner device to ask with — a missing `me` is a non-member head (name +
 * email, "My profile ›"), never an empty `aria-busy` line that waits forever. Pure.
 */
export function memberHeadPending(
  me: unknown,
  rememberedHandle: string | null,
  read: 'idle' | 'loading' | 'settled'
): boolean {
  return !me && Boolean(rememberedHandle) && read !== 'settled'
}

/**
 * The avatar menu's hub entries. `me` is resolved only when the menu opens (Constraint
 * a8b375cd) — until then, and for a reader without a member page, the entries go through
 * `/profile` (Settings: `/profile/settings`), which forward a member to the same hub tab anyway.
 * Gear carries the reader's published review count once `/me` said it — the design's bare
 * trailing figure ("2"), none at 0.
 */
export function accountMenuLinks(
  me: Parameters<typeof ownHubHandle>[0],
  profileHref: string,
  trailingSlash: boolean
): AccountMenuLinks {
  const hubHandle = ownHubHandle(me)
  const reviews = me ? me.shelf.filter(s => s.note.status === 'published').length : 0
  const meta = reviews > 0 ? reviews.toLocaleString('en-US') : undefined
  if (!hubHandle) {
    return {
      hubHandle: null,
      gear: { href: `${profileHref}?tab=gear`, meta },
      settings: accountHref(profileHref, 'settings'),
      stats: null,
      overview: null,
      line: null,
    }
  }
  const standing = me?.community ?? null
  return {
    hubHandle,
    gear: { href: memberHubHref(hubHandle, trailingSlash, 'gear'), meta },
    settings: memberHubHref(hubHandle, trailingSlash, 'settings'),
    stats: memberHubHref(hubHandle, trailingSlash, 'stats'),
    overview: memberHubHref(hubHandle, trailingSlash),
    line: [
      ...(standing && typeof standing.points === 'number'
        ? [tierLabel(standing.tier), `${standing.points.toLocaleString('en-US')} pts`]
        : []),
      `@${hubHandle}`,
    ].join(' · '),
  }
}
